import {
	normalizeSocialLinkLogo,
	socialLinkListAtom,
	TQrColorScheme,
	TSocialLinkField,
	welcomeImageAtom,
	welcomeImageFileAtom,
} from 'features/generator/contentFormSections'
import { colorSchemeOptionListAtom } from 'features/generator/contentFormSections/client/ContentDesignForm'
import {
	socialInfoDescriptionAtom,
	socialInfoHeadlineAtom,
} from 'features/generator/contentFormSections/SocialInformationForm'
import { generateCustomPlatformId } from 'features/generator/contentFormSections/SocialLinksForm/CustomSocialLinkFields'
import { SOCIAL_PLATFORM_LIST } from 'features/generator/utils/socialPlatformsConfig'
import { atom } from 'jotai'
import { TUploadedMedia } from 'services/mediaStorage'
import { isOnServer } from 'utils/generalUtils'
import { getFileFromMedia } from 'utils/getFileFromMedia'

import {
	primaryColorAtom,
	secondaryColorAtom,
	selectedColorSchemeAtom,
} from '@/content-from-sections/client'

import { imageFileListAtom, imageListAtom } from '../../contentFormSections'
import { QR_TYPE } from '../../generatorTypes'

export type TSocialMediaContent = {
	type: QR_TYPE.SocialMedia
	colors: Omit<TQrColorScheme, 'name'>
	gallery: TUploadedMedia[]
	info: {
		headline: string
		description: string
	}
	socials: TSocialLinkField[]
	loaderImage: TUploadedMedia
}

export const socialMediaContentAtom = atom<
	TSocialMediaContent,
	[TSocialMediaContent],
	void
>(
	(get) => ({
		type: QR_TYPE.SocialMedia,
		colors: {
			primary: get(primaryColorAtom),
			secondary: get(secondaryColorAtom),
		},
		gallery: get(imageListAtom),
		info: {
			headline: get(socialInfoHeadlineAtom),
			description: get(socialInfoDescriptionAtom) || '',
		},
		socials: get(socialLinkListAtom).map(
			({ name, url, description = '', logo }) => {
				return {
					name,
					url,
					description,
					logo: normalizeSocialLinkLogo(logo),
				}
			},
		),
		loaderImage:
			get(welcomeImageAtom) ||
			({
				publicId: '',
				resourceType: '',
				format: '',
				bytes: 0,
			} as TUploadedMedia),
	}),
	(get, set, content) => {
		// Color scheme
		const colorScheme = get(colorSchemeOptionListAtom).find(
			(scheme) =>
				scheme.primary === content.colors.primary &&
				scheme.secondary === content.colors.secondary,
		)

		set(selectedColorSchemeAtom, colorScheme?.name ?? '')
		set(primaryColorAtom, content.colors.primary)
		set(secondaryColorAtom, content.colors.secondary)

		// Images
		set(imageListAtom, content.gallery)
		if (!isOnServer) {
			setTimeout(async () => {
				try {
					const imageFileList = await Promise.all(
						content.gallery.map(async (image) => await getFileFromMedia(image)),
					)
					set(imageFileListAtom, imageFileList)
				} catch (error) {
					console.error(error)
				}
			}, 0)
		}

		// Socials
		const match = (name: string) =>
			SOCIAL_PLATFORM_LIST.find(
				(platform) => platform.name.toLowerCase() === name.toLowerCase(),
			)
		set(
			socialLinkListAtom,
			content.socials.map(({ name, url, description, logo }) => ({
				platformId: match(name)?.id ?? generateCustomPlatformId(),
				name,
				url,
				description,
				logo,
			})),
		)

		// Information
		set(socialInfoHeadlineAtom, content.info.headline)
		set(socialInfoDescriptionAtom, content.info.description)

		// Welcome screen
		set(welcomeImageAtom, content.loaderImage)
		if (!isOnServer) {
			setTimeout(async () => {
				try {
					const loaderFile = await getFileFromMedia(content.loaderImage)
					set(welcomeImageFileAtom, loaderFile)
				} catch (error) {
					console.error(error)
				}
			}, 0)
		}
	},
)
